import { useEffect, useRef, useMemo } from "react";

export function QuebecTopoMap() {
  const svgRef = useRef(null);
  const offsetRef = useRef(0);
  const rafRef = useRef(null);

  // Generate contour paths that loosely trace Québec's shape
  // Québec: rough bounding — tall province, wide at top (Ungava), narrows at St. Lawrence
  const contours = useMemo(() => {
    const layers = [];
    const seed = 42;
    const pseudoRandom = (n) => {
      const x = Math.sin(seed + n * 127.1) * 43758.5453;
      return x - Math.floor(x);
    };

    // Québec outline anchor points (normalized 0-1, viewBox 0-1000)
    // Simplified province silhouette from south (St. Lawrence) to north (Ungava Bay)
    const baseShape = [
      // Southern tip — Gaspé / Estrie
      { x: 0.62, y: 0.95 },
      { x: 0.55, y: 0.88 },
      { x: 0.48, y: 0.82 },
      // St. Lawrence corridor / Montréal
      { x: 0.40, y: 0.78 },
      { x: 0.38, y: 0.72 },
      // Laurentians / Saguenay
      { x: 0.35, y: 0.65 },
      { x: 0.30, y: 0.58 },
      // Côte-Nord
      { x: 0.32, y: 0.50 },
      { x: 0.35, y: 0.42 },
      // James Bay western shore
      { x: 0.25, y: 0.35 },
      { x: 0.22, y: 0.28 },
      { x: 0.25, y: 0.22 },
      // Ungava — wide top
      { x: 0.30, y: 0.15 },
      { x: 0.38, y: 0.10 },
      { x: 0.50, y: 0.07 },
      { x: 0.62, y: 0.08 },
      { x: 0.70, y: 0.12 },
      // Eastern coast — Labrador border
      { x: 0.72, y: 0.20 },
      { x: 0.70, y: 0.30 },
      { x: 0.68, y: 0.40 },
      { x: 0.65, y: 0.50 },
      { x: 0.63, y: 0.60 },
      { x: 0.65, y: 0.70 },
      { x: 0.68, y: 0.80 },
      { x: 0.66, y: 0.90 },
    ];

    for (let ring = 0; ring < 12; ring++) {
      const scale = 1 - ring * 0.055;
      const points = baseShape.map((p, i) => {
        const jitterX = (pseudoRandom(ring * 100 + i * 7) - 0.5) * 0.04 * (ring + 1);
        const jitterY = (pseudoRandom(ring * 200 + i * 13) - 0.5) * 0.03 * (ring + 1);
        const cx = 0.48, cy = 0.50;
        return {
          x: ((p.x - cx) * scale + cx + jitterX) * 1000,
          y: ((p.y - cy) * scale + cy + jitterY) * 1000,
        };
      });

      // Build smooth cubic bezier path
      let d = `M ${points[0].x},${points[0].y}`;
      for (let i = 0; i < points.length; i++) {
        const p0 = points[(i - 1 + points.length) % points.length];
        const p1 = points[i];
        const p2 = points[(i + 1) % points.length];
        const p3 = points[(i + 2) % points.length];

        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;

        d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
      }
      d += " Z";

      layers.push({
        d,
        opacity: 0.35 - ring * 0.02,
        strokeWidth: ring < 3 ? 1.0 : 0.7,
        stroke: ring < 2 ? '#4a4030' : '#444444',
        delay: ring * 0.15,
      });
    }

    // Add internal "elevation" contour lines (smaller closed loops inside)
    for (let c = 0; c < 6; c++) {
      const cx = 0.42 + pseudoRandom(c * 31) * 0.18;
      const cy = 0.35 + pseudoRandom(c * 47) * 0.30;
      const rx = 30 + pseudoRandom(c * 67) * 60;
      const ry = 25 + pseudoRandom(c * 83) * 50;
      const pts = 8;
      let d = "";
      const contourPoints = [];
      for (let i = 0; i < pts; i++) {
        const angle = (i / pts) * Math.PI * 2;
        const jitter = 1 + (pseudoRandom(c * 500 + i * 17) - 0.5) * 0.5;
        contourPoints.push({
          x: cx * 1000 + Math.cos(angle) * rx * jitter,
          y: cy * 1000 + Math.sin(angle) * ry * jitter,
        });
      }
      d = `M ${contourPoints[0].x},${contourPoints[0].y}`;
      for (let i = 0; i < contourPoints.length; i++) {
        const p0 = contourPoints[(i - 1 + contourPoints.length) % contourPoints.length];
        const p1 = contourPoints[i];
        const p2 = contourPoints[(i + 1) % contourPoints.length];
        const p3 = contourPoints[(i + 2) % contourPoints.length];
        const cp1x = p1.x + (p2.x - p0.x) / 5;
        const cp1y = p1.y + (p2.y - p0.y) / 5;
        const cp2x = p2.x - (p3.x - p1.x) / 5;
        const cp2y = p2.y - (p3.y - p1.y) / 5;
        d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
      }
      d += " Z";
      layers.push({
        d,
        opacity: 0.15 + pseudoRandom(c * 99) * 0.1,
        strokeWidth: 0.5,
        stroke: '#444444',
        delay: 1.5 + c * 0.2,
      });
    }

    return layers;
  }, []);

  useEffect(() => {
    const animate = () => {
      offsetRef.current += 0.08;
      if (svgRef.current) {
        svgRef.current.style.transform = `translate(${Math.sin(offsetRef.current * 0.01) * 8}px, ${Math.cos(offsetRef.current * 0.008) * 6}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ opacity: 1 }}>
      <svg
        ref={svgRef}
        viewBox="0 0 1000 1000"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        style={{ willChange: "transform" }}
      >
        {contours.map((contour, i) => (
          <path
            key={i}
            d={contour.d}
            fill="none"
            stroke={contour.stroke || '#444444'}
            strokeWidth={contour.strokeWidth || 0.7}
            style={{
              opacity: contour.opacity,
              strokeDasharray: "5000",
              strokeDashoffset: "5000",
              animation: `topoDrawIn 3s ease-out ${contour.delay}s forwards`,
            }}
          />
        ))}
      </svg>
    </div>
  );
}
